import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchJson } from "@/lib/api";

export function useWarehouses() {
  return useQuery({
    queryKey: ["warehouses"],
    queryFn: () => fetchJson<any[]>("/api/warehouse"),
  });
}

export function useCreateWarehouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; description?: string; position?: number }) =>
      fetchJson("/api/warehouse", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["warehouses"] }),
  });
}

export function useUpdateWarehouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; name?: string; description?: string; position?: number }) =>
      fetchJson(`/api/warehouse/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["warehouses"] }),
  });
}

export function useDeleteWarehouse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchJson(`/api/warehouse/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["warehouses"] }),
  });
}

export function useWarehouseBins(warehouseId: string | null, shelf?: number) {
  const params = new URLSearchParams();
  if (shelf !== undefined) params.set("shelf", String(shelf));
  return useQuery({
    queryKey: ["warehouse-bins", warehouseId, shelf],
    queryFn: () => fetchJson<any[]>(`/api/warehouse/${warehouseId}/bins?${params}`),
    enabled: !!warehouseId,
  });
}

export function useCreateShelf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ warehouseId, ...body }: { warehouseId: string; shelf: number; columns: number; rows: number }) =>
      fetchJson(`/api/warehouse/${warehouseId}/bins`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["warehouse-bins"] }),
  });
}

export function useDeleteShelf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ warehouseId, shelf }: { warehouseId: string; shelf: number }) =>
      fetchJson(`/api/warehouse/${warehouseId}/shelf/${shelf}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["warehouse-bins"] }),
  });
}

export function useDeleteBin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (binId: number) =>
      fetchJson(`/api/warehouse/bins/${binId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["warehouse-bins"] }),
  });
}

export function useAddSkuLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { sku: string; binCode: string; warehouseId: string }) =>
      fetchJson<any>("/api/warehouse/locations", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["warehouse-locations"] }),
  });
}

export function useRemoveSkuLocation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchJson(`/api/warehouse/locations/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["warehouse-locations"] }),
  });
}

export function useSearchLocations(
  query: string,
  warehouseId: string,
  page: number,
  sort: "newest" | "oldest" | "default" = "default",
  limit = 50,
) {
  const params = new URLSearchParams({ page: String(page), limit: String(limit) });
  if (query) params.set("q", query);
  if (warehouseId) params.set("warehouseId", warehouseId);
  if (sort !== "default") params.set("sort", sort);
  return useQuery({
    queryKey: ["warehouse-locations", query, warehouseId, page, sort, limit],
    queryFn: () => fetchJson<any>(`/api/warehouse/locations?${params}`),
  });
}

export function useSkuLocations(sku: string | null) {
  return useQuery({
    queryKey: ["warehouse-locations", "sku", sku],
    queryFn: () => fetchJson<any[]>(`/api/warehouse/locations/sku/${sku}`),
    enabled: !!sku,
  });
}

export function useMoveSkuLocations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { locationIds: string[]; destinationBinCode: string }) =>
      fetchJson<{ moved: number; destinationBin: { label: string; warehouseName: string } }>(
        "/api/warehouse/locations/move",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["warehouse-locations"] });
    },
  });
}
